import { AxiosError } from 'axios';
import { usePermissionModal } from 'contexts/PermissionModalContext';
import { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';

/**
 * Hook para facilitar o uso do PermissionModalContext com o React Query
 */
export function usePermissionQueryOptions<TData, TError = AxiosError>(
  queryKey: unknown[] | any,
  defaultMessage: string,
  options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'retry'>
) {
  const { getQueryRetryHandler } = usePermissionModal();

  return {
    queryKey,
    retry: getQueryRetryHandler({
      queryKey,
      defaultMessage
    }),
    ...options
  } as UseQueryOptions<TData, TError>;
}

/**
 * Hook para facilitar o uso do PermissionModalContext com o React Query (mutations)
 */
export function usePermissionMutation<TData, TError = AxiosError, TVariables = unknown, TContext = unknown>(
  defaultMessage: string,
  options?: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'onError'>
) {
  const { getMutationErrorHandler } = usePermissionModal();

  return {
    onError: getMutationErrorHandler(defaultMessage),
    ...options
  } as UseMutationOptions<TData, TError, TVariables, TContext>;
}

/**
 * Hook para facilitar o uso do PermissionModalContext com o React Query (mutations) com tratamento de erro personalizado
 */
export function usePermissionMutationWithErrorHandler<TData, TError = AxiosError, TVariables = unknown, TContext = unknown>(
  defaultMessage: string,
  errorHandler: (error: TError) => void,
  options?: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'onError'>
) {
  const { getMutationErrorHandler } = usePermissionModal();

  return {
    onError: getMutationErrorHandler(defaultMessage, errorHandler),
    ...options
  } as UseMutationOptions<TData, TError, TVariables, TContext>;
}
